import React, { useState, useCallback } from 'react';
import { Icon, Text } from '@mercator-gcp/grout';
import { FlexRow } from 'components/layout/FlexRow';
import AddProjectToCollectionModal from 'components/widgets/SaveProject/AddProjectToCollectionModal';
import DisplayUnsavedProject from 'components/widgets/SaveProject/DisplayUnsavedProject';
import SavedMenu from 'components/widgets/SaveProject/SavedMenu';
import { useHiddenProjects } from 'hooks/useHiddenProjects';
import { useSavedProjects } from 'hooks/useSavedProjects';
import { useToast } from 'hooks/useToast';
import { useCurrentLeadQuery } from 'state/queryParams';
import { CategoryEnum, SavedProject } from 'types/SavedProject';
import type { ButtonProps } from '@mercator-gcp/grout';

const optionListStyle = (useLargeSpace: boolean) => ({
  columnGap: useLargeSpace ? 'padding_8' : 'padding_4',
  justifyContent: 'flex-start',
});

const undoHideStyle = {
  columnGap: 'padding_8',
};

type TSaveProjectProps = {
  projectSlug?: string;
  documentId?: string;
  savedProject?: SavedProject;
  /**
   * Controls whether each menu renders bordered (styled) buttons. Defaults to
   * borders on for both. e.g. the leads table shows borders for the saved menu
   * but not the unsaved menu, while the PDP shows borders for both.
   */
  displayBorders?: {
    savedMenu?: boolean;
    unsavedMenu?: boolean;
  };
  sx?: ButtonProps['sx'];
  showLabel?: boolean;
  isSharedCollection?: boolean;
};

const SaveProject = ({
  projectSlug,
  documentId,
  savedProject,
  displayBorders = {},
  sx = {},
  showLabel = true,
  isSharedCollection = false,
}: TSaveProjectProps) => {
  const [addToCollectionOpen, setAddToCollectionOpen] = useState(false);
  const [projectCategory, setProjectCategory] = useState('');
  const { isProjectHidden, hideProject, unhideProject } = useHiddenProjects();
  const { selectSavedProjectBySlug } = useSavedProjects();
  const [, setLead] = useCurrentLeadQuery();

  const { renderUndoToast } = useToast();

  const savedMenuDisplaysBorders = displayBorders.savedMenu ?? true;
  const unsavedMenuDisplaysBorders = displayBorders.unsavedMenu ?? true;
  const unsavedButtonVariant = unsavedMenuDisplaysBorders ? 'styled' : 'unstyled';

  const thisSavedProject =
    savedProject || selectSavedProjectBySlug(projectSlug || documentId || '');
  const isWatched = thisSavedProject?.category === CategoryEnum.WATCH;
  const isStarred = thisSavedProject?.category === CategoryEnum.STAR;
  const isHidden = isProjectHidden(projectSlug || documentId || '');

  const handleUndoHideProject = () => {
    unhideProject.mutate(projectSlug!);
  };

  const blurButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
  };

  const handleHideProjectClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      hideProject.mutate({
        projectSlug,
        documentId,
        currentProjectCategory: thisSavedProject?.category || 'uncategorized',
      });
      renderUndoToast({
        toastTitle: 'Project Passed',
        onClickUndo: handleUndoHideProject,
        children: (
          <FlexRow sx={undoHideStyle}>
            <Icon name={Icon.Name.EyeHide} />
            <Text>Project Passed</Text>
          </FlexRow>
        ),
      });
      blurButton(e);
      // unset selected lead in case this action is coming from the lead table
      setLead('');
    },
    [hideProject, projectSlug, documentId, thisSavedProject?.category],
  );

  const handleWatchProjectClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setProjectCategory(CategoryEnum.WATCH);
      setAddToCollectionOpen(true);
      blurButton(e);
    },
    [setProjectCategory, setAddToCollectionOpen],
  );

  const handleStarProjectClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setProjectCategory(CategoryEnum.STAR);
      setProjectCategory(CategoryEnum.STAR);
      setAddToCollectionOpen(true);
      blurButton(e);
    },
    [setProjectCategory, setAddToCollectionOpen],
  );

  const addToCollectionOpenFn = useCallback(() => {
    setAddToCollectionOpen(false);
  }, [setAddToCollectionOpen]);

  if (isWatched || isStarred || isHidden) {
    // Hacking around a race condition where the Project Hide on a Starred/Watched project
    // And the resultant unWatch/unStar action from the Project Hide does not resolve quickly enough for this button.
    const isWatchedOverride = isWatched && !isHidden;
    const isStarredOverride = isStarred && !isHidden;
    // If a Project is Hidden, it overrides all other states.
    const isHiddenOverride = isHidden;

    return (
      <SavedMenu
        projectSlug={projectSlug}
        documentId={documentId}
        savedProject={thisSavedProject}
        isUnstyled={!savedMenuDisplaysBorders}
        sx={sx}
        showLabel={showLabel}
        isWatched={isWatchedOverride}
        isStarred={isStarredOverride}
        isHidden={isHiddenOverride}
        isSharedCollection={isSharedCollection}
      />
    );
  }

  return (
    <>
      {addToCollectionOpen && (
        <AddProjectToCollectionModal
          isOpen={addToCollectionOpen}
          onClose={addToCollectionOpenFn}
          projectCategory={projectCategory}
          projectSlug={projectSlug}
          documentId={documentId}
        />
      )}
      <FlexRow sx={optionListStyle(unsavedMenuDisplaysBorders)}>
        <DisplayUnsavedProject
          label="Qualify"
          variant={unsavedButtonVariant}
          onClick={handleStarProjectClick}
          icon={<Icon name={Icon.Name.Star} />}
          sx={sx}
        />
        <DisplayUnsavedProject
          label="Monitor"
          variant={unsavedButtonVariant}
          onClick={handleWatchProjectClick}
          icon={<Icon name={Icon.Name.Eye} />}
          sx={sx}
        />
        <DisplayUnsavedProject
          label="Pass"
          variant={unsavedButtonVariant}
          onClick={handleHideProjectClick}
          icon={<Icon name={Icon.Name.EyeHide} />}
          sx={sx}
        />
      </FlexRow>
    </>
  );
};

export default SaveProject;
